export const REFLECT_STAFF_ROLES = [
  'facilitator',
  'counselor',
  'admin',
  'ltbb_admin',
];

export const REFLECT_LEAD_ROLES = ['counselor', 'admin', 'ltbb_admin'];

export const parseRosterPaste = (value = '') => {
  const seen = new Set();

  return String(value || '')
    .split(/\r?\n/)
    .map((name) => name.trim())
    .filter((name) => {
      if (!name) return false;
      const key = name.toLocaleLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const STATUS_ORDER = {
  pending: 0,
  waiting: 0,
  unclaimed: 1,
  approved: 2,
};

export const sortRosterByStatus = (roster = []) => [...roster].sort((first, second) => {
  const firstOrder = STATUS_ORDER[first.status] ?? 3;
  const secondOrder = STATUS_ORDER[second.status] ?? 3;
  if (firstOrder !== secondOrder) return firstOrder - secondOrder;
  return String(first.display_name || '').localeCompare(
    String(second.display_name || ''),
    undefined,
    { sensitivity: 'base' },
  );
});

export const formatTimeAgo = (value, now = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  const current = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(date.getTime()) || Number.isNaN(current.getTime())) return 'Unknown time';

  const seconds = Math.max(0, Math.floor((current.getTime() - date.getTime()) / 1000));
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === current.getFullYear() ? undefined : 'numeric',
  });
};

export const countOpenAlertsBySeverity = (alerts = []) => alerts.reduce(
  (counts, alert) => {
    if (alert.status !== 'open') return counts;
    counts.total += 1;
    if (alert.severity === 'urgent') counts.urgent += 1;
    if (alert.severity === 'review') counts.review += 1;
    return counts;
  },
  { total: 0, urgent: 0, review: 0 },
);

export const isReflectStaff = (userOrRole) => {
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole?.role;
  return REFLECT_STAFF_ROLES.includes(role);
};

const ADMIN_ROLES = ['owner', 'admin', 'editor', 'ltbb_admin'];

// Where a signed-in account lands: admins on the dashboard, teachers and counselors
// on Reflect, anyone else back on the library.
export const staffHome = (user) => {
  if (user?.is_admin || ADMIN_ROLES.includes(user?.role)) return '/admin';
  if (isReflectStaff(user)) return '/admin/reflect';
  return '/';
};

export const isReflectLead = (userOrRole) => {
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole?.role;
  return REFLECT_LEAD_ROLES.includes(role);
};

export const reflectStaffErrorMessage = (status, detail = '') => {
  if (status === 401) return 'Your session ended. Sign in again.';
  if (status === 403) return 'This class belongs to another teacher.';
  if (status === 404) return "We couldn't find that item. It may have been deleted.";
  if (status === 409) {
    return detail || 'That name is already on this roster. Add a number to tell them apart.';
  }
  if (status === 429) return 'Too many requests at once. Wait a moment, then try again.';
  if (status >= 500) return 'Reflect is having trouble right now. Try again in a moment.';
  return detail || 'Something went wrong. Try again.';
};
